/**
 * MYAC-918 — how the conversation lists name time: which period a conversation
 * falls in (the history's headings) and when it was last touched, as a person
 * would say it. Ported from the mockup (front PR 868, `conversationView.ts`).
 */

import type { ConversationRight } from '@/stores/chatbot'

export type TimeGroup = 'today' | 'week' | 'month' | 'older'

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** Which period a conversation falls in, for the history's headings. */
export function timeGroup(ts: number, now: Date = new Date()): TimeGroup {
  const today = startOfDay(now)
  if (ts >= today) return 'today'
  if (ts >= today - 6 * 86_400_000) return 'week'
  if (ts >= today - 29 * 86_400_000) return 'month'
  return 'older'
}

export type WhenParts =
  | { key: 'now' }
  | { key: 'minutes'; n: number }
  | { key: 'hours'; n: number }
  | { key: 'yesterday' }
  | { key: 'weekday'; date: Date }
  | { key: 'date'; date: Date }

/** When a conversation was last touched, as a person would say it. */
export function whenParts(ts: number, now: Date = new Date()): WhenParts {
  const diff = now.getTime() - ts
  const today = startOfDay(now)
  if (diff < 60_000) return { key: 'now' }
  if (diff < 3_600_000 && ts >= today) return { key: 'minutes', n: Math.floor(diff / 60_000) }
  if (ts >= today) return { key: 'hours', n: Math.floor(diff / 3_600_000) }
  if (ts >= today - 86_400_000) return { key: 'yesterday' }
  if (ts >= today - 6 * 86_400_000) return { key: 'weekday', date: new Date(ts) }
  return { key: 'date', date: new Date(ts) }
}

/**
 * MYAC-1664 — who may rename a conversation: its owner and anyone who may write
 * in it (a named writer, or everyone on a project-wide write share), the rule the
 * backend's `rename` admission applies; a reader may not.
 */
export function mayRename(right: ConversationRight): boolean {
  return right === 'owner' || right === 'write'
}
